import React, { useState, useEffect } from "react";
import { View, StyleSheet, Pressable, ActivityIndicator } from "react-native";
import { Image } from "expo-image";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { AttachmentFile } from "../types";
import { formatFileSize, getFileIcon, getFileColor, createImageThumbnail } from "../lib/fileUtils";

interface FilePreviewProps {
  file: AttachmentFile;
  mode: "grid" | "list" | "compact";
  onRemove?: (fileId: string) => void;
  onPreview?: (file: AttachmentFile) => void;
  showProgress?: boolean;
  testID?: string;
}

export function FilePreview({ file, mode, onRemove, onPreview, showProgress = true, testID }: FilePreviewProps) {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  // Determine if this is a storage path that needs a signed URL
  const needsSignedUrl =
    file.uri && !file.uri.startsWith("blob:") && !file.uri.startsWith("data:") && !file.uri.startsWith("http");

  // Get signed URL for storage paths
  const { signedUrl, loading: urlLoading } = useSignedUrl({
    bucket: file.bucket || "projects", // Default to projects if no bucket specified
    path: needsSignedUrl ? file.uri : null,
    enabled: !!(needsSignedUrl && file.category === "image"),
    transform: {
      width: 200,
      quality: 80,
    },
  });

  // Generate thumbnail for local images (blob URLs)
  useEffect(() => {
    if (file.category === "image" && file.file && !thumbnail && !needsSignedUrl) {
      setThumbnailLoading(true);
      createImageThumbnail(file.file, 200)
        .then(setThumbnail)
        .catch(() => {
          // Fallback to original image
          setThumbnail(file.uri);
        })
        .finally(() => setThumbnailLoading(false));
    }
  }, [file, thumbnail, needsSignedUrl]);

  const handleRemove = () => {
    if (onRemove) {
      onRemove(file.id);
    }
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview(file);
    }
  };

  const renderPreview = () => {
    if (file.category === "image") {
      // Use signed URL if needed, otherwise use thumbnail or direct URI
      const imageUri = needsSignedUrl ? signedUrl : thumbnail || file.uri;
      const isLoading = thumbnailLoading || urlLoading;

      return (
        <View style={styles.imageContainer}>
          {isLoading ? (
            <View style={[styles.imagePlaceholder, { backgroundColor: colors.backgroundSecondary }]}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          ) : imageUri ? (
            <Image source={imageUri} style={styles.image} contentFit="cover" transition={200} />
          ) : (
            <View style={[styles.imagePlaceholder, { backgroundColor: colors.backgroundSecondary }]}>
              <MaterialCommunityIcons name="image-off" size={32} color={colors.textSecondary} />
            </View>
          )}
        </View>
      );
    }

    return (
      <View style={[styles.iconContainer, { backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons
          name={getFileIcon(file) as any}
          size={mode === "compact" ? 24 : 32}
          color={getFileColor(file)}
        />
      </View>
    );
  };

  const renderProgress = () => {
    if (!showProgress || !file.uploadProgress || file.uploadStatus === "completed") {
      return null;
    }

    return (
      <View style={styles.progressContainer}>
        <View style={[styles.progressBackground, { backgroundColor: colors.backgroundSecondary }]}>
          <View
            style={[
              styles.progressBar,
              {
                backgroundColor: colors.primary,
                width: `${file.uploadProgress}%`,
              },
            ]}
          />
        </View>
        <ThemedText style={styles.progressText}>{Math.round(file.uploadProgress)}%</ThemedText>
      </View>
    );
  };

  const renderControls = () => {
    if (!isHovered && mode !== "list") return null;

    return (
      <View style={styles.controls}>
        {onPreview && (
          <Button
            icon="eye"
            style={[styles.controlButton, { backgroundColor: colors.primary }]}
            onPress={handlePreview}
            testID={`${testID}-preview`}
          />
        )}
        {onRemove && (
          <Button
            icon="trash-can"
            style={[styles.controlButton, { backgroundColor: colors.dangerText }]}
            onPress={handleRemove}
            testID={`${testID}-remove`}
          />
        )}
      </View>
    );
  };

  const renderFileInfo = () => {
    const showFullInfo = mode === "list" || (mode === "grid" && isHovered);

    return (
      <View style={styles.fileInfo}>
        <ThemedText style={[styles.fileName, { color: colors.text }]} numberOfLines={mode === "compact" ? 1 : 2}>
          {file.name}
        </ThemedText>

        {showFullInfo && (
          <>
            <ThemedText style={[styles.fileSize, { color: colors.textSecondary }]}>
              {formatFileSize(file.size)}
            </ThemedText>

            {file.uploadStatus === "error" && file.error && (
              <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{file.error}</ThemedText>
            )}
          </>
        )}
      </View>
    );
  };

  if (mode === "compact") {
    return (
      <Pressable
        testID={testID}
        style={[styles.compactContainer, { borderBottomColor: colors.border }]}
        onPress={handlePreview}
      >
        {renderPreview()}
        <View style={styles.compactInfo}>
          {renderFileInfo()}
          {renderProgress()}
        </View>
        {renderControls()}
      </Pressable>
    );
  }

  if (mode === "list") {
    return (
      <Pressable
        testID={testID}
        style={[styles.listContainer, { borderBottomColor: colors.border }]}
        onPress={handlePreview}
      >
        {renderPreview()}
        <View style={styles.listInfo}>
          {renderFileInfo()}
          {renderProgress()}
        </View>
        {renderControls()}
      </Pressable>
    );
  }

  // Grid mode
  return (
    <Pressable
      testID={testID}
      style={[styles.gridContainer, { backgroundColor: colors.card }]}
      onPress={handlePreview}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      <View style={styles.gridPreview}>
        {renderPreview()}
        {renderControls()}
      </View>

      <View style={styles.gridInfo}>
        {renderFileInfo()}
        {renderProgress()}
      </View>

      {file.uploadStatus && file.uploadStatus !== "completed" && (
        <View style={styles.statusIndicator}>
          <MaterialCommunityIcons
            name={
              file.uploadStatus === "uploading" ? "upload" : file.uploadStatus === "error" ? "alert-circle" : "clock"
            }
            size={16}
            color={file.uploadStatus === "error" ? colors.dangerText : colors.primary}
          />
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Compact mode styles
  compactContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 12,
  },
  compactInfo: {
    flex: 1,
    gap: 4,
  },

  // List mode styles
  listContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 16,
  },
  listInfo: {
    flex: 1,
    gap: 4,
  },

  // Grid mode styles
  gridContainer: {
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    minWidth: 150,
    maxWidth: 200,
  },
  gridPreview: {
    position: "relative",
    aspectRatio: 4 / 3,
  },
  gridInfo: {
    padding: 12,
    gap: 4,
  },

  // Preview styles
  imageContainer: {
    flex: 1,
    borderRadius: 4,
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
  },
  imagePlaceholder: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },

  // File info styles
  fileInfo: {
    gap: 2,
  },
  fileName: {
    fontSize: 14,
    fontWeight: "500",
  },
  fileSize: {
    fontSize: 12,
  },
  errorText: {
    fontSize: 11,
    fontStyle: "italic",
  },

  // Progress styles
  progressContainer: {
    gap: 4,
  },
  progressBackground: {
    height: 4,
    borderRadius: 2,
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: 2,
  },
  progressText: {
    fontSize: 10,
    textAlign: "right",
  },

  // Controls styles
  controls: {
    position: "absolute",
    top: 8,
    right: 8,
    flexDirection: "row",
    gap: 6,
  },
  controlButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: "center",
    alignItems: "center",
  },

  // Status indicator
  statusIndicator: {
    position: "absolute",
    top: 8,
    left: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
});
